import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { FrozenColumnScroll } from '../layout/FrozenColumnScroll';
import { colors, typography } from '../../core/theme/tokens';
import { formatCurrency } from '../charts/d3/format';
import {
  INTEREST_COLOR,
  PRINCIPAL_COLOR,
  type CompoundYearRow,
} from '../../utils/investmentCompounding';

type CompoundYearTableProps = {
  rows: CompoundYearRow[];
  /** Highlight the year growth first exceeds contributions. */
  kickInYear?: number | null;
  /** Show every year (Grow tab). Default: every other year after year 0. */
  dense?: boolean;
  maxRows?: number;
};

function formatK(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return formatCurrency(value);
}

export function CompoundYearTable({
  rows,
  kickInYear = null,
  dense = false,
  maxRows,
}: CompoundYearTableProps) {
  const filtered = dense
    ? rows
    : rows.filter((r) => r.yearIndex === 0 || r.yearIndex % 2 === 0);
  const display = maxRows != null ? filtered.slice(0, maxRows) : filtered;

  return (
    <FrozenColumnScroll
      frozenWidth={64}
      frozen={
        <View>
          <View style={styles.tableHeader}>
            <Text style={[styles.th, styles.colYear]}>Year</Text>
          </View>
          {display.map((row) => {
            const isKickIn = kickInYear != null && row.year === kickInYear;
            return (
              <View
                key={row.year}
                style={[styles.tableRow, isKickIn && styles.kickInRow]}
              >
                <Text style={[styles.td, styles.colYear, isKickIn && styles.kickInText]}>
                  {row.year}
                  {isKickIn ? ' ★' : ''}
                </Text>
              </View>
            );
          })}
        </View>
      }
      scrollable={
        <View>
          <View style={styles.tableHeader}>
            <Text style={[styles.th, styles.colMoney]}>Principal</Text>
            <Text style={[styles.th, styles.colMoney]}>Interest</Text>
            <Text style={[styles.th, styles.colMoney]}>Contrib</Text>
            <Text style={[styles.th, styles.colMoney, styles.highlight]}>Total</Text>
          </View>
          {display.map((row) => {
            const isKickIn = kickInYear != null && row.year === kickInYear;
            return (
              <View
                key={row.year}
                style={[styles.tableRow, isKickIn && styles.kickInRow]}
              >
                <Text style={[styles.td, styles.colMoney, styles.principal]}>
                  {formatK(row.principalInvested)}
                </Text>
                <Text style={[styles.td, styles.colMoney, styles.interest]}>
                  {row.cumulativeGrowth > 0 ? formatK(row.cumulativeGrowth) : '—'}
                </Text>
                <Text style={[styles.td, styles.colMoney]}>
                  {row.contributions > 0 ? formatK(row.contributions) : '—'}
                </Text>
                <Text style={[styles.td, styles.colMoney, styles.highlight]}>
                  {formatK(row.endBalance)}
                </Text>
              </View>
            );
          })}
        </View>
      }
    />
  );
}

const styles = StyleSheet.create({
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 28,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 32,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  kickInRow: {
    backgroundColor: 'rgba(61, 214, 164, 0.1)',
    borderRadius: 6,
  },
  th: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
    fontSize: 10,
  },
  td: {
    ...typography.caption,
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  colYear: {
    width: 64,
  },
  colMoney: {
    width: 76,
    textAlign: 'right',
  },
  highlight: {
    color: colors.accent,
    fontWeight: '600',
  },
  principal: {
    color: PRINCIPAL_COLOR,
  },
  interest: {
    color: INTEREST_COLOR,
  },
  kickInText: {
    color: colors.accent,
    fontWeight: '700',
  },
});
